import { useEffect, useState } from 'react';
import { getServiceSummary } from '../api/api';
import styles from './AnalyticsSummaryModal.module.scss';

const STRINGS = {
  en: {
    eyebrow: 'Fleet analytics',
    title: 'Support overview – Service statistics',
    serviceTime: 'Total service time',
    serviceTimeCaption: 'on-site service time in period',
    travelDistance: 'Travel distance',
    travelCaption: value => `avg ${value} per visit`,
    visits: 'Service visits',
    visitsCaption: count => `across ${count} machine${count === 1 ? '' : 's'}`,
    avgVisit: 'Avg time per visit',
    avgVisitCaption: 'service time ÷ visits',
    panelTime: 'Most service time',
    panelDistance: 'Longest travel distance',
    visitCount: count => `${count} visit${count === 1 ? '' : 's'}`,
    rankedTime: 'Ranked by total service time in the period. One service note = one visit.',
    rankedDistance: 'Ranked by total travel distance logged on service notes in the period.',
    noService: 'No service notes recorded in this scope.',
    loading: 'Loading service statistics…',
    failed: 'Failed to load service statistics.',
  },
  sv: {
    eyebrow: 'Flottanalys',
    title: 'Supportöversikt – servicestatistik',
    serviceTime: 'Total servicetid',
    serviceTimeCaption: 'servicetid på plats i perioden',
    travelDistance: 'Resesträcka',
    travelCaption: value => `snitt ${value} per besök`,
    visits: 'Servicebesök',
    visitsCaption: count => `fördelat på ${count} maskin${count === 1 ? '' : 'er'}`,
    avgVisit: 'Snittid per besök',
    avgVisitCaption: 'servicetid ÷ besök',
    panelTime: 'Mest servicetid',
    panelDistance: 'Längst resesträcka',
    visitCount: count => `${count} besök`,
    rankedTime: 'Rangordnat efter total servicetid i perioden. En serviceanteckning = ett besök.',
    rankedDistance: 'Rangordnat efter total resesträcka i periodens serviceanteckningar.',
    noService: 'Inga serviceanteckningar registrerade i detta urval.',
    loading: 'Laddar servicestatistik…',
    failed: 'Det gick inte att ladda servicestatistiken.',
  },
};

function formatDecimal(value, locale) {
  return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function RankedPanel({ number, title, footnote, emptyText, rows, renderValue, visitLabel }) {
  const max = rows.length ? rows[0].value : 0;
  return (
    <div className={styles.panel}>
      <h3>
        <span>{number}</span> {title}
      </h3>
      <div className={`${styles.panelBody} ${styles.rankedBody}`}>
        {rows.length === 0 && <p className={styles.empty}>{emptyText}</p>}
        {rows.map((row, index) => (
          <div key={row.machine.machine_id} className={styles.rankedRow}>
            <span className={styles.rank}>#{index + 1}</span>
            <div className={styles.machineInfo}>
              <strong>{row.machine.nickname || row.machine.serial}</strong>
              {row.machine.nickname && <small>{row.machine.serial}</small>}
            </div>
            <div className={styles.barTrack}>
              <div
                className={`${styles.barFill} ${styles.barGreen}`}
                style={{ width: `${max > 0 ? (row.value / max) * 100 : 0}%` }}
              />
            </div>
            <div className={styles.rankedValue}>
              <strong>{renderValue(row.value)}</strong>
              <small>{visitLabel(row.machine.service_count)}</small>
            </div>
          </div>
        ))}
      </div>
      <p className={styles.footnote}>{footnote}</p>
    </div>
  );
}

// Second page of the analytics summary: service notes aggregated over the same
// period and machine scope as the operation/error slide.
function AnalyticsServiceSlide({ scope, language, locale, periodLabel }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const t = STRINGS[language];

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    getServiceSummary({
      startDate: scope.startDate,
      endDate: scope.endDate,
      machineIds: scope.machineIds.length ? scope.machineIds : null,
    })
      .then(response => {
        if (!cancelled) {
          setData(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const hours = minutes => `${formatDecimal(minutes / 60, locale)} h`;
  const km = value => `${formatDecimal(value, locale)} km`;

  const visits = data?.service_count || 0;
  const totalMinutes = data?.total_minutes || 0;
  const totalKm = data?.total_travel_distance_km || 0;
  const emptyText = failed ? t.failed : data ? t.noService : t.loading;

  return (
    <>
      <header className={styles.slideHeader}>
        <div>
          <span className={styles.eyebrow}>{t.eyebrow}</span>
          <h2>{t.title}</h2>
        </div>
        <span className={styles.scopeLine}>{periodLabel}</span>
      </header>

      <div className={styles.kpiRow}>
        <div className={`${styles.kpi} ${styles.kpiGreen}`}>
          <span className={styles.kpiLabel}>{t.serviceTime}</span>
          <strong className={styles.kpiValue}>{hours(totalMinutes)}</strong>
          <span className={styles.kpiCaption}>{t.serviceTimeCaption}</span>
        </div>
        <div className={`${styles.kpi} ${styles.kpiGreen}`}>
          <span className={styles.kpiLabel}>{t.travelDistance}</span>
          <strong className={styles.kpiValue}>{km(totalKm)}</strong>
          <span className={styles.kpiCaption}>
            {t.travelCaption(km(visits > 0 ? totalKm / visits : 0))}
          </span>
        </div>
        <div className={`${styles.kpi} ${styles.kpiGreen}`}>
          <span className={styles.kpiLabel}>{t.visits}</span>
          <strong className={styles.kpiValue}>{visits.toLocaleString(locale)}</strong>
          <span className={styles.kpiCaption}>{t.visitsCaption(data?.machine_count || 0)}</span>
        </div>
        <div className={`${styles.kpi} ${styles.kpiGreen}`}>
          <span className={styles.kpiLabel}>{t.avgVisit}</span>
          <strong className={styles.kpiValue}>
            {hours(visits > 0 ? totalMinutes / visits : 0)}
          </strong>
          <span className={styles.kpiCaption}>{t.avgVisitCaption}</span>
        </div>
      </div>

      <div className={`${styles.panelRow} ${styles.panelRowTwo}`}>
        <RankedPanel
          number="01"
          title={t.panelTime}
          footnote={t.rankedTime}
          emptyText={emptyText}
          rows={(data?.top_service_time_machines || []).map(machine => ({
            machine,
            value: machine.total_minutes,
          }))}
          renderValue={hours}
          visitLabel={t.visitCount}
        />
        <RankedPanel
          number="02"
          title={t.panelDistance}
          footnote={t.rankedDistance}
          emptyText={emptyText}
          rows={(data?.top_travel_distance_machines || []).map(machine => ({
            machine,
            value: machine.travel_distance_km,
          }))}
          renderValue={km}
          visitLabel={t.visitCount}
        />
      </div>
    </>
  );
}

export default AnalyticsServiceSlide;
